/**
 * Minimal promise-based IndexedDB wrapper with an in-memory fallback.
 *
 * All stores live in one database ("conquer"). Each record must have a string `id`.
 * When IndexedDB is unavailable (private mode, old browsers, tests) or fails to open,
 * every store transparently falls back to a Map for the rest of the session.
 */

export interface KVStore<T extends { id: string }> {
  list(): Promise<T[]>;
  get(id: string): Promise<T | undefined>;
  put(value: T): Promise<void>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
  /** True when data is persisted in IndexedDB, false for the memory fallback. */
  isPersistent(): Promise<boolean>;
}

export const DB_NAME = "conquer";
export const DB_VERSION = 1;
/** All object stores (keyPath "id"). Add new names here and bump DB_VERSION. */
export const STORE_NAMES = ["replayMeta", "replayData", "mapMeta", "mapData", "thumbnails"] as const;
export type StoreName = (typeof STORE_NAMES)[number];

const FALLBACK = Symbol("fallback");
type Fallback = typeof FALLBACK;

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let idb: IDBFactory | undefined;
    try {
      idb = typeof indexedDB !== "undefined" ? indexedDB : undefined;
    } catch {
      idb = undefined;
    }
    if (!idb) {
      resolve(null);
      return;
    }
    let settled = false;
    const done = (db: IDBDatabase | null) => {
      if (settled) return;
      settled = true;
      resolve(db);
    };
    // Some browsers hang forever on open in private mode: time out to the fallback.
    const timer = setTimeout(() => done(null), 4000);
    try {
      const req = idb.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const name of STORE_NAMES) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: "id" });
        }
      };
      req.onsuccess = () => {
        clearTimeout(timer);
        const db = req.result;
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        done(db);
      };
      req.onerror = () => {
        clearTimeout(timer);
        done(null);
      };
      req.onblocked = () => {
        clearTimeout(timer);
        done(null);
      };
    } catch {
      clearTimeout(timer);
      done(null);
    }
  });
  return dbPromise;
}

const memoryStores = new Map<StoreName, Map<string, { id: string }>>();
function memoryStore<T extends { id: string }>(name: StoreName): Map<string, T> {
  let s = memoryStores.get(name);
  if (!s) {
    s = new Map();
    memoryStores.set(name, s);
  }
  return s as Map<string, T>;
}

/** Structured clone that degrades gracefully where `structuredClone` is missing. */
function clone<T>(v: T): T {
  try {
    return structuredClone(v);
  } catch {
    return v;
  }
}

/**
 * Run one request in its own transaction. Resolves with the request result after the
 * transaction completed (for writes), or FALLBACK when IndexedDB is unavailable.
 */
async function run<R>(name: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<R>): Promise<R | Fallback> {
  const db = await openDB();
  if (!db) return FALLBACK;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(name, mode);
  } catch {
    // e.g. the DB was closed by a version change; reads fall back, writes report failure.
    if (mode === "readwrite") throw new Error("IndexedDB unavailable");
    return FALLBACK;
  }
  return new Promise<R | Fallback>((resolve, reject) => {
    let result: R;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => (mode === "readwrite" ? reject(tx.error ?? new Error("IndexedDB transaction failed")) : resolve(FALLBACK));
    tx.onabort = () => (mode === "readwrite" ? reject(tx.error ?? new Error("IndexedDB transaction aborted")) : resolve(FALLBACK));
    try {
      const req = fn(tx.objectStore(name));
      req.onsuccess = () => {
        result = req.result;
      };
    } catch (err) {
      if (mode === "readwrite") reject(err);
      else resolve(FALLBACK);
    }
  });
}

export function openStore<T extends { id: string }>(name: StoreName): KVStore<T> {
  const mem = () => memoryStore<T>(name);
  return {
    async list() {
      const r = await run(name, "readonly", (s) => s.getAll() as IDBRequest<T[]>);
      if (r === FALLBACK) return Array.from(mem().values()).map(clone);
      return r;
    },
    async get(id) {
      const r = await run(name, "readonly", (s) => s.get(id) as IDBRequest<T | undefined>);
      if (r === FALLBACK) {
        const v = mem().get(id);
        return v === undefined ? undefined : clone(v);
      }
      return r;
    },
    async put(value) {
      const r = await run(name, "readwrite", (s) => s.put(value));
      if (r === FALLBACK) mem().set(value.id, clone(value));
    },
    async delete(id) {
      const r = await run(name, "readwrite", (s) => s.delete(id));
      if (r === FALLBACK) mem().delete(id);
    },
    async clear() {
      const r = await run(name, "readwrite", (s) => s.clear());
      if (r === FALLBACK) mem().clear();
    },
    async isPersistent() {
      return (await openDB()) !== null;
    },
  };
}

/** Test helper: forget the cached DB handle and memory stores. */
export function _resetIdbForTests(): void {
  dbPromise = null;
  memoryStores.clear();
}
